/**
 * WebSocket client for /api/v1/monitoring/live with reconnect.
 *
 * Connection state is exposed as a small observable so the UI can light
 * a status dot. Reconnect uses exponential backoff capped at 30 s.
 */

export type WSState = "connecting" | "open" | "closed";

// DeltaEvent meldet einen abgeschlossenen Delta-Lauf (2-Min-Sync) mit der
// aktuellen Change-Tracking-Version.
export interface DeltaEvent {
  type: "delta";
  version: number;
  time: string;
  changed: boolean; // true = dieser Lauf hat etwas verarbeitet (sonst Leerlauf)
}

// JobEvent meldet Start/Ende eines Sync-Jobs (Live-Indikator).
export interface JobEvent {
  type: "job";
  running: boolean;
  kind?: "full" | "delta";
  started_at?: string;
}

export type LiveMessage = DeltaEvent | JobEvent;

// Backoff schedule in milliseconds (1s, 2s, 5s, 10s, 30s clamp)
const BACKOFF_STEPS = [1000, 2000, 5000, 10000, 30000];

export interface LiveClient {
  /** Stop reconnecting and close the socket. */
  close(): void;
}

export function connectLive(
  onMessage: (msg: LiveMessage) => void,
  onState: (s: WSState) => void,
): LiveClient {
  let attempt = 0;
  let ws: WebSocket | null = null;
  let closedByUser = false;
  let reconnectTimer: number | null = null;

  // Resolve the WS URL relative to current page. Honours TLS scheme,
  // so the same code works behind https/wss reverse proxies.
  const wsUrl = (() => {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${window.location.host}/api/v1/monitoring/live`;
  })();

  function scheduleReconnect() {
    if (closedByUser) return;
    const delay = BACKOFF_STEPS[Math.min(attempt, BACKOFF_STEPS.length - 1)];
    attempt += 1;
    onState("closed");
    reconnectTimer = window.setTimeout(open, delay);
  }

  function open() {
    onState("connecting");
    ws = new WebSocket(wsUrl);
    ws.onopen = () => {
      attempt = 0;
      onState("open");
    };
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data?.type === "delta" || data?.type === "job") {
          onMessage(data as LiveMessage);
        }
      } catch {
        // ignore malformed frames
      }
    };
    ws.onclose = () => {
      ws = null;
      scheduleReconnect();
    };
    ws.onerror = () => {
      // onclose follows automatically; nothing to do here
    };
  }

  open();

  return {
    close() {
      closedByUser = true;
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      ws?.close();
    },
  };
}
